const randomUUID = () => globalThis.crypto.randomUUID()
import { submitInterWorkerMessage, callViaHTTP } from './api.js'
import {
  packInterWorkerMessage,
  unpackInterWorkerMessageData,
} from './inter_worker_message.js'
import {
  unpackWorkerRequestPayload,
  validateWorkerRequest,
} from './worker_request.js'
import WaitMap from './wait_map.js'
import {
  MESSAGE_TYPES,
  TIMING_CONSTANTS,
  TRANSPORT_MODES,
  ERROR_CODES,
  LOCAL_CALL_TARGET,
} from './constants.js'
import { bytesToHex } from './utils.js'

/**
 * @typedef {import('./types.js').RPCContext} RPCContext
 * @typedef {import('./types.js').CallOptions} CallOptions
 */

/**
 * Creates an RPC executor for inter-worker communication
 *
 * @param {Object} params
 * @param {Object<string, Function>} params.sandbox - Available RPC methods
 * @param {string} params.serviceUrl - Workers-channel service URL
 * @param {Uint8Array} params.privKey - This worker's Ed25519 private key
 * @param {Uint8Array} params.publicKey - This worker's Ed25519 public key
 * @param {number} params.callTimeout - Default RPC call timeout (ms)
 * @param {string} params.transport - Transport mode ('ws', 'http', or 'auto')
 * @param {Function} params.wsConnected - Function that returns WebSocket connection state
 * @returns {Object} Executor instance with RPC methods
 */
export default ({
  sandbox,
  serviceUrl,
  privKey,
  publicKey,
  callTimeout,
  transport = TRANSPORT_MODES.AUTO,
  wsConnected = () => false,
}) => {
  const waitMap = WaitMap()

  /**
   * Validates RPC call parameters
   *
   * @param {string} receiverPublicKey - Receiver's public key
   * @param {string} method - Method name
   * @param {Array} params - Method parameters
   * @throws {Error} If validation fails
   */
  const validateCallParams = (receiverPublicKey, method, params) => {
    if (!receiverPublicKey) {
      throw new Error('receiver_public_key is required')
    }
    if (!method) {
      throw new Error('method is required')
    }
    if (!Array.isArray(params)) {
      throw new Error(`params must be an array, got: ${typeof params}`)
    }
  }

  // Pre-compute self public key hex for local call optimization
  const selfPublicKeyHex = bytesToHex(publicKey).toLowerCase()

  /**
   * Makes an RPC call to another worker
   *
   * @param {string} receiver_public_key - Receiver's public key (hex)
   * @param {string} method - Method name to call on receiver
   * @param {Array<*>} [params=[]] - Method parameters
   * @param {CallOptions} [opts={}] - Call options
   * @returns {Promise<*>} Response data from receiver
   * @throws {Error} If call fails or times out
   */
  const call = async (receiver_public_key, method, params = [], opts = {}) => {
    // Validate parameters
    validateCallParams(receiver_public_key, method, params)

    // Local call optimization: skip network if calling self or using 'local' target
    if (receiver_public_key === LOCAL_CALL_TARGET || receiver_public_key.toLowerCase() === selfPublicKeyHex) {
      const ctx = {
        request: { public_key: selfPublicKeyHex },
        headers: opts.headers || {},
      }
      return callSandboxMethod(ctx, method, params)
    }

    // Determine transport mode
    let selectedTransport = opts.transport || transport

    if (selectedTransport === TRANSPORT_MODES.AUTO) {
      // Auto mode: prefer WS if connected, fallback to HTTP
      selectedTransport = wsConnected()
        ? TRANSPORT_MODES.WS
        : TRANSPORT_MODES.HTTP
    }

    // Call via selected transport
    try {
      if (selectedTransport === TRANSPORT_MODES.HTTP) {
        return await callViaHTTPTransport(receiver_public_key, method, params, opts)
      } else {
        return await callViaWSTransport(receiver_public_key, method, params, opts)
      }
    } catch (err) {
      err.receiverPublicKey = receiver_public_key
      err.method = method
      if (!err.message.includes(receiver_public_key)) {
        err.message = `call ${receiver_public_key}::${method}: ${err.message}`
      }
      throw err
    }
  }

  /**
   * Makes an RPC call via WebSocket transport
   *
   * @param {string} receiver_public_key - Receiver's public key (hex)
   * @param {string} method - Method name
   * @param {Array<*>} params - Method parameters
   * @param {CallOptions} opts - Call options
   * @returns {Promise<*>} Response data
   * @throws {Error} If WebSocket not connected or call fails
   */
  const callViaWSTransport = async (
    receiver_public_key,
    method,
    params,
    opts,
  ) => {
    if (!wsConnected()) {
      throw new Error(
        'WebSocket not connected, use transport: "http" or enable WebSocket',
      )
    }

    // Generate unique request ID for matching request/response
    const request_id = randomUUID()

    // Calculate message expiration time (2 minutes from now)
    const expireAt = new Date(
      Date.now() + TIMING_CONSTANTS.MESSAGE_EXPIRE_TIME,
    ).toISOString()

    // Pack and encrypt the request message
    const encryptedMessage = await packInterWorkerMessage(privKey, {
      publicKey,
      receiver_public_key,
      data: {
        type: MESSAGE_TYPES.REQUEST,
        request_id,
        method,
        params,
        headers: opts.headers || {},
      },
      expire_at: expireAt,
    })

    // Submit request to relay server
    await submitInterWorkerMessage({
      publicKey,
      privKey,
      serviceUrl,
      payload: encryptedMessage,
    })

    // Wait for response (Promise will resolve when response arrives)
    return new Promise((resolve, reject) => {
      waitMap.addRequest({
        request_id,
        resolve,
        reject,
        timeout: opts.callTimeout || callTimeout,
        locator: `${receiver_public_key}::${method}`,
      })
    })
  }

  /**
   * Makes an RPC call via HTTP transport
   *
   * @param {string} receiver_public_key - Receiver's public key (hex)
   * @param {string} method - Method name
   * @param {Array<*>} params - Method parameters
   * @param {CallOptions} opts - Call options
   * @returns {Promise<*>} Response data
   * @throws {Error} If call fails
   */
  const callViaHTTPTransport = async (
    receiver_public_key,
    method,
    params,
    opts,
  ) => {
    return callViaHTTP({
      publicKey,
      privKey,
      serviceUrl,
      receiver: receiver_public_key,
      method,
      params,
      headers: opts.headers || {},
      timeout: opts.callTimeout || callTimeout,
    })
  }

  /**
   * Processes an incoming inter-worker message
   *
   * @param {import('./types.js').WorkerRequest} request - Worker request to process
   * @returns {Promise<void>}
   * @throws {Error} If message validation or processing fails
   */
  const processInterWorkerMessage = async (request) => {
    // Validate request has required fields
    validateWorkerRequest(request)

    // Unpack and verify signature
    const message = unpackWorkerRequestPayload(request)

    // Decrypt message data
    const data = await unpackInterWorkerMessageData(privKey, message)

    // Route based on message type
    switch (data.type) {
      case MESSAGE_TYPES.REQUEST:
        // Handle incoming RPC request
        return processRequestMessage(request, data)

      case MESSAGE_TYPES.RESPONSE:
        // Handle RPC response
        return processResponseMessage(request, data)

      default:
        throw new Error(`unknown message type: ${data.type}`)
    }
  }

  /**
   * Processes an incoming RPC request
   *
   * @param {import('./types.js').WorkerRequest} request - Original worker request
   * @param {import('./types.js').RequestMessageData} messageData - Decrypted message data
   * @returns {Promise<void>}
   */
  const processRequestMessage = async (
    request,
    { request_id, method, params, headers = {} },
  ) => {
    let responseData = null
    let error = null

    // Try to execute the requested method
    try {
      responseData = await callSandboxMethod(
        { request, headers },
        method,
        params,
      )
    } catch (e) {
      error = e
    }

    // Determine error code
    let code = ERROR_CODES.SUCCESS
    if (error) {
      code = error.code || ERROR_CODES.INTERNAL_ERROR
    }

    // Calculate response expiration
    const expireAt = new Date(
      Date.now() + TIMING_CONSTANTS.MESSAGE_EXPIRE_TIME,
    ).toISOString()

    // Pack and encrypt response message
    const encryptedResponse = await packInterWorkerMessage(privKey, {
      publicKey,
      receiver_public_key: request.public_key,
      data: {
        type: MESSAGE_TYPES.RESPONSE,
        request_id,
        code,
        message: error ? error.toString() : '',
        data: responseData,
      },
      expire_at: expireAt,
    })

    // Add request_id to outer message for HTTP call routing
    const responsePayload = {
      ...encryptedResponse,
      request_id,
    }

    // Submit response to relay server
    await submitInterWorkerMessage({
      publicKey,
      privKey,
      serviceUrl,
      payload: responsePayload,
    })
  }

  /**
   * Processes an incoming RPC response
   *
   * @param {import('./types.js').WorkerRequest} request - Original worker request
   * @param {import('./types.js').ResponseMessageData} messageData - Decrypted message data
   * @returns {Promise<void>}
   */
  const processResponseMessage = async (
    request,
    { request_id, code, message, data },
  ) => {
    // Check if response indicates error
    if (code !== ERROR_CODES.SUCCESS) {
      waitMap.rejectRequest(request_id, code, message)
      return
    }

    // Resolve with successful response data
    waitMap.resolveRequest(request_id, data)
  }

  /**
   * Calls a method in the sandbox
   *
   * @param {RPCContext} ctx - RPC context (request metadata)
   * @param {string} method - Method name to call
   * @param {Array<*>} params - Method parameters
   * @returns {Promise<*>} Method return value
   * @throws {Error} If method not found in sandbox
   */
  const callSandboxMethod = (ctx, method, params) => {
    const fn = sandbox[method]

    if (!fn) {
      const error = new Error(`method not found: ${method}`)
      error.code = ERROR_CODES.NOT_FOUND
      throw error
    }

    return fn(ctx, ...params)
  }

  return {
    call,
    callSandboxMethod,
    processInterWorkerMessage,

    // Legacy export (deprecated, use processInterWorkerMessage)
    processInnerWorkerMessage: processInterWorkerMessage,
  }
}
